import { useMutation } from '@tanstack/react-query';
import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import type { TelegramLoginWidgetInput } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { useEffect, useRef } from 'react';
import { z } from 'zod';
import { exchangeLoginWidget } from '../../../features/auth/api';
import { useAuth } from '../../../features/auth/auth-context';
import { defaultAdminHome } from '../../../features/auth/default-admin-home';
import { SupportContactBlock } from '../../../shared/components/support-contact';

// Telegram приклеивает к URL поля widget'а через query: id, first_name, ...
// Парсим тем же контрактом, что и body endpoint'а — payload идентичный.
// auth_date / id приходят как строки → coerce в number.
const searchSchema = z.object({
  id: z.coerce.number().int().positive(),
  first_name: z.string().min(1),
  last_name: z.string().optional(),
  username: z.string().optional(),
  photo_url: z.url().optional(),
  auth_date: z.coerce.number().int().positive(),
  hash: z.string().regex(/^[a-f0-9]{64}$/),
  redirect: z.string().optional(),
});

export const Route = createFileRoute('/_public/auth/telegram-callback')({
  validateSearch: searchSchema,
  component: TelegramCallbackPage,
});

function TelegramCallbackPage() {
  const { setSession } = useAuth();
  const router = useRouter();
  const search = Route.useSearch();
  // StrictMode дважды монтирует — гард, чтобы не отправлять exchange два раза
  // с одним и тем же auth_date (второй вернёт UnauthorizedException или 403).
  const sentRef = useRef(false);

  const exchange = useMutation({
    mutationFn: (payload: TelegramLoginWidgetInput) => exchangeLoginWidget(payload),
    onSuccess: (data) => {
      setSession({ accessToken: data.accessToken, user: data.user, adminRoles: data.adminRoles });
      router.navigate({ to: search.redirect ?? defaultAdminHome(data.adminRoles) });
    },
  });

  useEffect(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    const { redirect: _redirect, ...payload } = search;
    exchange.mutate(payload);
  }, [exchange, search]);

  if (exchange.isError) {
    const status = (exchange.error as { response?: { status?: number } })?.response?.status;
    const message =
      status === 403
        ? 'У этого Telegram-аккаунта нет прав на админку.'
        : status === 401
          ? 'Ссылка устарела или подпись недействительна. Откройте /start admin_login в боте заново.'
          : 'Не удалось войти. Попробуйте ещё раз.';
    return (
      <div className="w-full max-w-sm p-6 border rounded-lg bg-card">
        <h1 className="text-xl font-semibold mb-2">Вход не удался</h1>
        <p className="text-sm text-muted-foreground mb-4">{message}</p>
        <SupportContactBlock className="mb-6" />
        <Button asChild variant="outline" className="w-full">
          <Link to="/login">Вернуться на /login</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm p-6 border rounded-lg bg-card">
      <h1 className="text-xl font-semibold mb-2">Входим…</h1>
      <p className="text-sm text-muted-foreground">Проверяем подпись Telegram. Это занимает секунду.</p>
    </div>
  );
}
